'use client'

import { Plus, Search } from 'lucide-react'

import { cn } from '../cn'
import { useShellChrome, useSidebarState } from './app-shell'
import { SHELL_CIRCLE_CLASS } from './mobile-top-row'

/**
 * Marks a visible search trigger. `@ordo/agent-embed` springs its search
 * open from (and closes back into) the first laid-out element carrying it
 * when its own top-bar pill is hidden, as it is on phones.
 */
export const SHELL_SEARCH_ANCHOR_ATTR = 'data-shell-search-anchor'

export interface MobileFloatingActionsProps {
  /** Opens the app's search (e.g. `useAgentOpen()` from `@ordo/agent-embed`). Omit to hide the circle. */
  onSearch?: () => void
  searchLabel?: string
  className?: string
}

/**
 * Phone-only bottom-right circles for the unified shell: search, with the
 * page's blue + stacked directly above it when the page registered one via
 * `useShellCreateAction`. Both tuck away while the menu drawer or the app
 * grid is open.
 */
export function MobileFloatingActions({ onSearch, searchLabel = 'Search', className }: MobileFloatingActionsProps) {
  const { createAction, appGridOpen } = useShellChrome()
  const sidebar = useSidebarState()
  if (!onSearch && !createAction) {
    return null
  }
  const tucked = appGridOpen || Boolean(sidebar?.open)
  return (
    <div
      className={cn(
        'fixed bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] right-4 z-40 flex flex-col items-center gap-3 transition-[transform,opacity] duration-300 ease-out md:hidden',
        tucked && 'pointer-events-none translate-y-[calc(100%+2rem)] opacity-0',
        className,
      )}>
      {createAction ? (
        <button
          type="button"
          aria-label={createAction.label}
          onClick={createAction.onCreate}
          className={cn(
            SHELL_CIRCLE_CLASS,
            'border-primary bg-primary text-primary-foreground hover:bg-primary/90 [&_svg]:h-6 [&_svg]:w-6',
          )}>
          <Plus strokeWidth={2.5} />
        </button>
      ) : null}
      {onSearch ? (
        <button
          type="button"
          aria-label={searchLabel}
          onClick={onSearch}
          className={SHELL_CIRCLE_CLASS}
          {...{ [SHELL_SEARCH_ANCHOR_ATTR]: '' }}>
          <Search />
        </button>
      ) : null}
    </div>
  )
}
